import React, { useState } from "react";
import { ArrowLeft, Home, Loader2, Wallet } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomerData } from "@/redux/slices/customerSlice";
import { dynamicAPI, RedeemVoucher, transferBalance } from "@/services/customer";
import { getT } from "@/i18n/translations";


const UpiConfirmPage = () => {
  const t = getT();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { upiId, upiName, defaultBank } = location.state || {};
  const { data: customer } = useSelector((state) => state.customer);

  const vouchers = customer?.WalletSummary?.filter((item) => item.type === "voucher") || [];
  const availableVouchers = vouchers.filter(
    (item) => item.VoucherUsed === false && item.earnedValue > 0
  );
  const available = availableVouchers.reduce((sum, item) => sum + (item.earnedValue || 0), 0);

  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [transferredAmount, setTransferredAmount] = useState(0);
  const [transferWarning, setTransferWarning] = useState(false);

  const handleFinalSubmit = async () => {
    if (available === 0) return;
    setLoading(true);

    try {
      const token_gen = JSON.parse(localStorage.getItem("token_gen"));
      const customerCode = localStorage.getItem("customercode");
      const now = new Date().toISOString();

      for (const v of availableVouchers) {
        const redeemBody = {
          customer: { customercode: customerCode },
          redeemDetail: {
            walletType: "Voucher",
            voucherCode: v.voucherCode,
            voucherTemplateCode: v.voucherTemplateCode,
            transactionType: "redeem",
            transactionDate: now,
            remarks: upiId
              ? `UPI: ${upiId}`
              : defaultBank
                ? `Bank: ${defaultBank.bankName} ****${defaultBank.accountNumber?.slice(-4)}`
                : "Transfer",
          },
        };

        const redeemRes = await RedeemVoucher(token_gen?.access_token, redeemBody);
        if (redeemRes?.data?.result !== 1) {
          toast.error(redeemRes?.data?.responceMessage || `Failed to redeem ${v.voucherCode}`);
          setLoading(false);
          return;
        }
      }

      let anyTransferFailed = false;
      const allVoucherCodes = availableVouchers.map(v => v.voucherCode).join(",");

      let transferBody = {};
      if (upiId) {
        transferBody = {
          beneficiaryType: "upi",
          beneficiaryName: upiName || "",
          upiHandle: upiId,
          paymentMode: "UPI",
          sourceType: "voucher",
          customerCode: customerCode,
          voucherCode: allVoucherCodes,
        };
      } else if (defaultBank) {
        transferBody = {
          beneficiaryType: "bank",
          beneficiaryName: defaultBank.nickname || defaultBank.bankName,
          accountNumber: defaultBank.accountNumber,
          ifscCode: defaultBank.ifscCode,
          bankName: defaultBank.bankName,
          paymentMode: "IMPS",
          sourceType: "voucher",
          customerCode: customerCode,
          voucherCode: allVoucherCodes,
        };
      }



      try {
        const metaPayload = {
          customer: {
            customerCode,
            name: customer.customer?.name,
            mobile: customer.customer?.mobile
          },
          vouchers: availableVouchers.map(v => ({
            voucherCode: v.voucherCode,
            voucherTemplateCode: v.voucherTemplateCode,
            earnedDate: v.earnedDate
          })),
          payment: upiId
            ? {
              type: "upi",
              upiId,
              upiName: upiName || "",
              totalAmount: available,
            }
            : defaultBank
              ? {
                type: "bank",
                bankName: defaultBank.bankName,
                accountNumber: defaultBank.accountNumber,
                ifscCode: defaultBank.ifscCode,
                beneficiaryName:
                  defaultBank.nickname || defaultBank.bankName,
                totalAmount: available,
              }
              : {},
          transactionDate: now,
          merchantId: customer.customer.merchantId[0]
        };

        await dynamicAPI(token_gen?.access_token, metaPayload);
      } catch (err) {
        console.error("Meta API failed", err);
      }

      await dispatch(fetchCustomerData(customerCode));

      setTransferredAmount(available);
      setTransferWarning(anyTransferFailed);
      setShowPopup(true);

    } catch {
      toast.error(t.confirm_errGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: "#f0f4f0",
        backgroundImage: "radial-gradient(circle, #c8d8c8 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    >
      {loading && (
        <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-50 backdrop-blur-sm">
          <Loader2 className="animate-spin h-10 w-10 text-[#2d5a2d]" />
        </div>
      )}

      <div className="w-full max-w-md min-h-screen flex flex-col">

        {/* Header */}
        <div className="relative flex items-center justify-center px-4 py-4 mt-2">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm"
          >
            <ArrowLeft className="w-5 h-5 text-[#1a2e1a]" />
          </button>
          <p className="text-[15px] font-semibold text-[#1a2e1a]">{t.confirm_pageTitle}</p>
          <button
            onClick={() => navigate("/home")}
            className="absolute right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm"
          >
            <Home className="w-5 h-5 text-[#1a2e1a]" />
          </button>
        </div>

        <div className="px-4 pb-8 space-y-3 flex-1">

          {/* Amount card */}
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm mx-1 text-center">
            <p className="text-[10px] font-semibold text-[#8a9e8a] uppercase tracking-widest mb-1">{t.confirm_transferAmount}</p>
            <p className="text-[48px] font-bold text-[#2d5a2d] leading-tight">₹{available}</p>
            {available === 0 && (
              <p className="text-[12px] text-red-400 mt-2">{t.confirm_noBalance}</p>
            )}
          </div>

          {/* Vouchers list */}
          {availableVouchers.length > 0 && (
            <div className="bg-white rounded-2xl px-5 py-4 shadow-sm mx-1">
              <p className="text-[10px] font-semibold text-[#8a9e8a] uppercase tracking-widest mb-3">{t.confirm_vouchersLabel}</p>
              <div className="space-y-2">
                {availableVouchers.map((v, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <p className="text-[12px] font-semibold text-[#1a2e1a]">{v.voucherCode}</p>
                    <p className="text-[13px] font-bold text-[#2d5a2d]">+₹{v.earnedValue}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* UPI card */}
          {upiId && (
            <div className="bg-white rounded-2xl px-5 py-4 shadow-sm mx-1">
              <p className="text-[10px] font-semibold text-[#8a9e8a] uppercase tracking-widest mb-3">{t.confirm_paymentMethod}</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#eaf3de] flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2d5a2d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="5" y="2" width="14" height="20" rx="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2.5" />
                  </svg>
                </div>
                <div>
                  <p className="text-[13px] font-bold text-[#1a2e1a]">{t.confirm_upi}</p>
                  {upiName && <p className="text-[12px] text-gray-500 font-medium">{upiName}</p>}
                  <p className="text-[12px] text-gray-400">{upiId}</p>
                </div>
              </div>
            </div>
          )}

          {/* Bank card */}
          {defaultBank && (
            <div className="bg-white rounded-2xl px-5 py-4 shadow-sm mx-1">
              <p className="text-[10px] font-semibold text-[#8a9e8a] uppercase tracking-widest mb-3">{t.confirm_paymentMethod}</p>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#eaf3de] flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2d5a2d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="5" width="20" height="14" rx="2" />
                    <path d="M16 12h.01" />
                  </svg>
                </div>
                <p className="text-[13px] font-bold text-[#1a2e1a]">{t.confirm_bankTransfer}</p>
              </div>
              <div className="space-y-1.5 border-t border-gray-50 pt-3">
                {[
                  { label: t.confirm_bank, value: defaultBank.bankName },
                  { label: t.confirm_account, value: `**** ${defaultBank.accountNumber?.slice(-4)}` },
                  { label: t.confirm_ifsc, value: defaultBank.ifscCode },
                  { label: t.confirm_name, value: defaultBank.nickname },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between">
                    <p className="text-[12px] text-gray-400">{label}</p>
                    <p className="text-[12px] font-semibold text-[#1a2e1a]">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Submit button */}
          <button
            onClick={handleFinalSubmit}
            disabled={loading || available === 0}
            className="w-full py-4 rounded-xl text-[15px] font-bold text-white active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ width: "calc(100% - 8px)", marginLeft: "4px", backgroundColor: "#2d5a2d" }}
          >
            {available === 0 ? t.confirm_btnNoBalance : t.confirm_btnConfirm}
          </button>



        </div>
        <div className="w-full py-3 border-t border-gray-200 mt-auto">
          <p className="text-[13px] text-black uppercase text-center tracking-wide">
            © {new Date().getFullYear()} Gencrest
          </p>
        </div>
      </div>
      {showPopup && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center px-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl p-7 text-center mx-4">

            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: "rgba(168,208,138,0.2)", border: "2px solid rgba(168,208,138,0.5)" }}
            >
              <Wallet className="w-7 h-7 text-[#2d5a2d]" />
            </div>

            <p className="text-[11px] font-semibold tracking-widest text-[#4a7c59] uppercase mb-1">{t.confirm_popupLabel}</p>
            <h2 className="text-[14px] font-bold text-[#1a2e1a] mb-1">{t.confirm_popupTitle}</h2>
            <p className="text-[10px] text-gray-400 mb-4">{t.confirm_popupMsg}</p>

            <div
              className="rounded-2xl py-4 px-5 mb-4"
              style={{ backgroundColor: "#f0f7e8", border: "1.5px solid #a8d08a" }}
            >
              <p className="text-[36px] font-bold text-[#2d5a2d] leading-tight">₹{transferredAmount}</p>
              <p className="text-[12px] text-[#4a7c59] mt-1">{t.confirm_popupTotal}</p>
            </div>

            {transferWarning && (
              <div
                className="rounded-xl px-4 py-3 mb-4 text-left"
                style={{ backgroundColor: "#fffbeb", border: "1.5px solid #fcd34d" }}
              >
                <p className="text-[12px] font-semibold text-amber-700 mb-0.5">{t.confirm_warningTitle}</p>
                <p className="text-[11px] text-amber-600 leading-relaxed">{t.confirm_warningMsg}</p>
              </div>
            )}

            <button
              onClick={() => navigate("/home", { replace: true })}
              className="w-full py-3.5 rounded-xl text-[15px] font-bold text-white active:scale-[0.98] transition-transform"
              style={{ backgroundColor: "#2d5a2d" }}
            >
              {t.confirm_done}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpiConfirmPage;